import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Header: React.FC = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    // Close mobile menu when route changes
    useEffect(() => {
        setIsMenuOpen(false);
    }, [location]);

    // Add scroll effect for header
    useEffect(() => {
        const handleScroll = () => {
            const isScrolled = window.scrollY > 10;
            if (isScrolled !== scrolled) {
                setScrolled(isScrolled);
            }
        };

        document.addEventListener('scroll', handleScroll, { passive: true });
        return () => document.removeEventListener('scroll', handleScroll);
    }, [scrolled]);

    const navLinks = [
        { to: "/", label: "Home" },
        { to: "/about", label: "About" },
        { to: "/programs", label: "Programs" },
        { to: "/gallery", label: "Gallery" },
        { to: "/contact", label: "Contact Us" },
    ];

    return (
        <header className={`sticky top-0 z-50 w-full transition-shadow duration-300 ${scrolled ? 'shadow-md bg-white/90 backdrop-blur-sm' : 'bg-white'}`}>
            {/* Top Contact Bar - Hidden on mobile */}
            <div className="hidden md:block w-full text-white" style={{ backgroundImage: 'linear-gradient(90deg, #da291c 0, #da291c 50%, #9a3324)' }}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
                    <div className="flex flex-col sm:flex-row justify-between items-center">
                        <div className="flex flex-wrap justify-center sm:justify-start gap-x-6 gap-y-2 text-xs sm:text-sm">
                            <a href="tel:+919876543210" className="flex items-center hover:opacity-90 transition-opacity">
                                <svg className="w-4 h-4 mr-1.5 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.79 19.79 0 012.08 4.2 2 2 0 014.06 2h3a2 2 0 012 1.72c.12.89.32 1.76.59 2.6a2 2 0 01-.45 2.11L8 9a16 16 0 006 6l.57-1.2a2 2 0 012.11-.45c.84.27 1.71.47 2.6.59A2 2 0 0122 16.92z"/>
                                </svg>
                                +91 98765 43210
                            </a>
                            <a href="mailto:info@jeevanjyotifoundation.org" className="flex items-center hover:opacity-90 transition-opacity">
                                <svg className="w-4 h-4 mr-1.5 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M2 6a2 2 0 012-2h16a2 2 0 012 2v.4l-10 6.25L2 6.4V6zm0 3.2V18a2 2 0 002 2h16a2 2 0 002-2V9.2l-9.37 5.86a2 2 0 01-2.26 0L2 9.2z"/>
                                </svg>
                                info@jeevanjyotifoundation.org
                            </a>
                        </div>
                        <div className="hidden sm:flex items-center gap-3 mt-2 sm:mt-0">
                            {['f', '𝕏', '◎', 'in'].map((icon, index) => (
                                <a key={index} href="#" className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 transition-colors flex items-center justify-center text-sm">
                                    {icon}
                                </a>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Navigation */}
            <div className="relative bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16 md:h-20">
                        {/* Logo */}
                        <Link to="/" className="flex-shrink-0 flex items-center">
                            <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-[#da291c] flex items-center justify-center text-white font-bold text-lg md:text-xl">
                                JJ
                            </div>
                            <div className="ml-3">
                                <div className="text-lg md:text-xl font-bold text-gray-900">Jeevan Jyoti</div>
                                <div className="text-xs md:text-sm text-gray-600">Foundation</div>
                            </div>
                        </Link>

                        {/* Desktop Navigation */}
                        <nav className="hidden md:flex items-center space-x-1 lg:space-x-4">
                            {navLinks.map((link) => (
                                <Link
                                    key={link.to}
                                    to={link.to}
                                    className={`px-3 py-2 rounded-md text-sm lg:text-base font-medium transition-colors ${
                                        location.pathname === link.to
                                            ? 'text-[#da291c] bg-red-50'
                                            : 'text-gray-700 hover:text-[#da291c] hover:bg-gray-50'
                                    }`}
                                >
                                    {link.label}
                                </Link>
                            ))}
                            <Link
                                to="/donate"
                                className="ml-2 lg:ml-4 px-4 py-2 rounded-full bg-[#da291c] text-white text-sm lg:text-base font-medium hover:bg-[#b82219] transition-colors"
                            >
                                Donate Now
                            </Link>
                        </nav>

                        {/* Mobile menu button */}
                        <div className="md:hidden flex items-center">
                            <button
                                onClick={() => setIsMenuOpen(!isMenuOpen)}
                                className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-[#da291c] hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[#da291c]"
                                aria-expanded="false"
                            >
                                <span className="sr-only">Open main menu</span>
                                {isMenuOpen ? (
                                    <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                ) : (
                                    <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                    </svg>
                                )}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Mobile menu */}
                <div className={`md:hidden transition-all duration-300 ease-in-out ${isMenuOpen ? 'max-h-96' : 'max-h-0 overflow-hidden'}`}>
                    <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white border-t">
                        {navLinks.map((link) => (
                            <Link
                                key={link.to}
                                to={link.to}
                                className={`block px-3 py-2 rounded-md text-base font-medium ${
                                    location.pathname === link.to
                                        ? 'bg-red-50 text-[#da291c]'
                                        : 'text-gray-700 hover:bg-gray-50 hover:text-[#da291c]'
                                }`}
                            >
                                {link.label}
                            </Link>
                        ))}
                        <Link
                            to="/donate"
                            className="block w-full text-center px-4 py-2 mt-2 rounded-md bg-[#da291c] text-white font-medium hover:bg-[#b82219]"
                        >
                            Donate Now
                        </Link>
                        <div className="flex justify-center space-x-4 pt-3 pb-2 border-t mt-3">
                            {['f', '𝕏', '◎', 'in'].map((icon, index) => (
                                <a key={index} href="#" className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-700">
                                    {icon}
                                </a>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </header>
    );
};

export default Header;